import { useId, useRef, useState } from 'react'
import { buildSeed, type AdultSetup, type ChildSetup } from '@domain/seed.ts'
import type { FamilyId, MemberId } from '@domain/ids.ts'
import type { EventStore } from '@application/ports.ts'
import type { HomeSession } from '@application/session.ts'
import { restaurarRegistro } from '../shell/restaurar.ts'
import { es } from '../strings/es.ts'

/**
 * El alta de la casa. Se hace una vez.
 *
 * Tres cosas que esta pantalla hacía y que estaban sencillamente mal:
 *
 * **Daba por hecho que se empieza de cero.** Quien cambia de tablet tenía que
 * crear la casa y luego importar la copia, y eso deja dos casas dentro del mismo
 * registro: dos veces cada hijo, y las monedas donde nadie las encuentra. Ahora lo
 * primero que ofrece es importar.
 *
 * **Daba por hecho dos hijos y un adulto.** Hay casas con tres hijos, con dos
 * madres, con un padre solo o con una abuela. El modelo ya lo admitía —`parent` no
 * es un número— y lo que faltaba era preguntarlo.
 *
 * **Sacaba el identificador del nombre** (`m-gabriel`). Dos tablets con el mismo
 * hijo generaban el mismo identificador, que parece cómodo hasta que se ve lo que
 * hacía: al importar, el alta más reciente reescribía al miembro con el saldo a
 * cero. Un nombre no es una identidad; ahora los identificadores los pone el mismo
 * generador que todo lo demás.
 */

const ACCENTS = ['#1c6a73', '#9c6c08', '#7d3c98', '#1a5c3a', '#a33a2a', '#2a4a8a'] as const
const EMOJIS = ['🦊', '🦉', '🐢', '🦁', '🐬', '🦔'] as const

interface Draft {
  name: string
  year: string
  literal: boolean
  glutenFree: boolean
}

const emptyChild = (): Draft => ({ name: '', year: '', literal: false, glutenFree: false })

export function Setup({
  session,
  store,
  onDone,
}: {
  session: HomeSession
  /** Para poder restaurar antes de que exista casa ninguna. */
  store: EventStore | null
  onDone: () => void
}) {
  const [adults, setAdults] = useState<string[]>([''])
  const [children, setChildren] = useState<Draft[]>([emptyChild()])
  const [busy, setBusy] = useState(false)
  const [aviso, setAviso] = useState<string | null>(null)
  const fileInput = useRef<HTMLInputElement>(null)

  const updateChild = (index: number, patch: Partial<Draft>): void => {
    setChildren((current) => current.map((c, i) => (i === index ? { ...c, ...patch } : c)))
  }

  const complete =
    adults.some((a) => a.trim().length > 0) &&
    children.every((c) => c.name.trim().length > 0 && /^\d{4}$/.test(c.year))

  const importar = async (file: File): Promise<void> => {
    if (!store) return
    setBusy(true)
    try {
      const hecho = await restaurarRegistro(store, file)
      if (!hecho.ok) {
        setAviso(es.setup.yaTengoNada)
        return
      }
      setAviso(es.setup.yaTengoHecho(hecho.puestos))
      onDone()
    } finally {
      setBusy(false)
    }
  }

  const create = async (): Promise<void> => {
    setBusy(true)
    try {
      const nombres = adults.map((a) => a.trim()).filter((a) => a.length > 0)
      const adultos: AdultSetup[] = nombres.map((name) => ({
        id: session.newId() as MemberId,
        name,
      }))
      const setups: ChildSetup[] = children.map((child, index) => ({
        id: session.newId() as MemberId,
        name: child.name.trim(),
        birthYear: Number(child.year),
        accent: ACCENTS[index % ACCENTS.length] ?? ACCENTS[0],
        emoji: EMOJIS[index % EMOJIS.length] ?? EMOJIS[0],
        literalLanguage: child.literal,
        glutenFree: child.glutenFree,
      }))
      const primero = adultos[0]
      if (!primero) return

      await session.seed(
        buildSeed({
          familyId: 'f-casa' as FamilyId,
          parentId: primero.id,
          parentName: primero.name,
          otherAdults: adultos.slice(1),
          timezone: 'Europe/Madrid',
          dayCutoffHour: 4,
          meetingDayOfWeek: 7,
          children: setups,
          now: new Date(),
          day: session.today(),
        }),
      )
      onDone()
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="mx-auto max-w-xl px-5 pt-10 pb-16">
      <h1 className="mb-2 text-4xl font-extrabold tracking-tight">{es.setup.title}</h1>
      <p className="text-muted mt-0 mb-6">{es.setup.explain}</p>

      <section className="border-gold bg-gold-soft mb-8 rounded-xl border-t-4 p-5">
        <h2 className="m-0 mb-1 text-base font-extrabold">{es.setup.yaTengo}</h2>
        <p className="m-0 mb-3 text-sm">{es.setup.yaTengoExplain}</p>
        <button
          type="button"
          disabled={busy || store === null}
          className="border-rule rounded-lg border bg-white px-5 font-bold disabled:opacity-50"
          onClick={() => fileInput.current?.click()}
        >
          {es.setup.yaTengoBoton}
        </button>
        <input
          ref={fileInput}
          type="file"
          accept="application/json"
          className="hidden"
          aria-label={es.setup.yaTengoBoton}
          onChange={(e) => {
            const file = e.target.files?.[0]
            if (file) void importar(file)
            e.target.value = ''
          }}
        />
        {aviso ? (
          <p role="status" className="m-0 mt-3 text-sm font-bold">
            {aviso}
          </p>
        ) : null}
      </section>

      <section className="border-teal bg-teal-soft mb-8 rounded-xl border-t-4 p-5">
        <h2 className="m-0 mb-2 text-base font-extrabold">🔒 {es.privacy.title}</h2>
        <p className="m-0 font-bold">{es.privacy.body}</p>
        <p className="m-0 mt-1">{es.privacy.consequence}</p>
        <p className="text-muted m-0 mt-2 text-sm">{es.privacy.backup}</p>
      </section>

      <h2 className="text-muted mt-0 mb-1 text-xs font-bold tracking-[0.1em] uppercase">
        {es.setup.oEmpezar}
      </h2>

      <h3 className="mt-6 mb-1 text-lg font-extrabold">{es.setup.adults}</h3>
      <p className="text-muted mt-0 mb-3 text-sm">{es.setup.adultsExplain}</p>
      {adults.map((name, index) => (
        <AdultField
          key={index}
          index={index}
          name={name}
          canRemove={adults.length > 1}
          onChange={(value) => {
            setAdults((current) => current.map((a, i) => (i === index ? value : a)))
          }}
          onRemove={() => {
            setAdults((current) => current.filter((_, i) => i !== index))
          }}
        />
      ))}
      <button
        type="button"
        className="border-rule mb-8 rounded-lg border px-4 text-sm font-bold"
        onClick={() => {
          setAdults((current) => [...current, ''])
        }}
      >
        {es.setup.addAdult}
      </button>

      <h3 className="mt-2 mb-3 text-lg font-extrabold">{es.setup.children}</h3>
      {children.map((child, index) => (
        <ChildFields
          key={index}
          index={index}
          child={child}
          canRemove={children.length > 1}
          onChange={(patch) => {
            updateChild(index, patch)
          }}
          onRemove={() => {
            setChildren((current) => current.filter((_, i) => i !== index))
          }}
        />
      ))}
      <button
        type="button"
        className="border-rule rounded-lg border px-4 text-sm font-bold"
        onClick={() => {
          setChildren((current) => [...current, emptyChild()])
        }}
      >
        {es.setup.addChild}
      </button>

      <button
        type="button"
        disabled={!complete || busy}
        onClick={() => {
          void create()
        }}
        className="bg-teal mt-8 w-full rounded-xl px-6 py-4 text-lg font-extrabold text-white disabled:opacity-40"
      >
        {busy ? es.setup.creating : es.setup.create}
      </button>
    </div>
  )
}

function AdultField({
  index,
  name,
  canRemove,
  onChange,
  onRemove,
}: {
  index: number
  name: string
  canRemove: boolean
  onChange: (value: string) => void
  onRemove: () => void
}) {
  const id = useId()
  return (
    <div className="mb-3">
      <label className="mb-1 block text-sm font-bold" htmlFor={id}>
        {es.setup.adultName(index + 1)}
      </label>
      <div className="flex gap-2">
        <input
          id={id}
          className="border-rule bg-surface flex-1 rounded-lg border px-4"
          value={name}
          onChange={(e) => {
            onChange(e.target.value)
          }}
        />
        {canRemove ? (
          <button
            type="button"
            className="border-rule rounded-lg border px-4 text-sm font-bold"
            onClick={onRemove}
          >
            {es.setup.removeAdult}
          </button>
        ) : null}
      </div>
    </div>
  )
}

function ChildFields({
  index,
  child,
  canRemove,
  onChange,
  onRemove,
}: {
  index: number
  child: Draft
  canRemove: boolean
  onChange: (patch: Partial<Draft>) => void
  onRemove: () => void
}) {
  const nameId = useId()
  const yearId = useId()
  const literalId = useId()
  const glutenId = useId()

  return (
    <fieldset className="border-rule bg-surface mb-6 rounded-xl border p-5">
      <legend className="text-teal px-2 text-sm font-bold tracking-wide uppercase">
        {es.setup.childNumber(index + 1)}
      </legend>

      <label className="mb-1 block text-sm font-bold" htmlFor={nameId}>
        {es.setup.childName(index + 1)}
      </label>
      <input
        id={nameId}
        className="border-rule mb-4 w-full rounded-lg border bg-white px-4"
        value={child.name}
        onChange={(e) => {
          onChange({ name: e.target.value })
        }}
      />

      <label className="mb-1 block text-sm font-bold" htmlFor={yearId}>
        {es.setup.childYear}
      </label>
      <input
        id={yearId}
        className="border-rule mb-4 w-full rounded-lg border bg-white px-4"
        inputMode="numeric"
        maxLength={4}
        value={child.year}
        onChange={(e) => {
          onChange({ year: e.target.value.replace(/\D/g, '') })
        }}
      />

      <div className="mb-3 flex items-start gap-3">
        <input
          id={literalId}
          type="checkbox"
          className="mt-3 h-6 w-6 min-h-0 shrink-0"
          checked={child.literal}
          onChange={(e) => {
            onChange({ literal: e.target.checked })
          }}
        />
        <label htmlFor={literalId}>
          <span className="block font-bold">{es.setup.literal}</span>
          <span className="text-muted block text-sm">{es.setup.literalHelp}</span>
        </label>
      </div>

      <div className="flex items-start gap-3">
        <input
          id={glutenId}
          type="checkbox"
          className="mt-3 h-6 w-6 min-h-0 shrink-0"
          checked={child.glutenFree}
          onChange={(e) => {
            onChange({ glutenFree: e.target.checked })
          }}
        />
        <label htmlFor={glutenId}>
          <span className="block font-bold">{es.setup.glutenFree}</span>
          <span className="text-muted block text-sm">{es.setup.glutenFreeHelp}</span>
        </label>
      </div>

      {canRemove ? (
        <button
          type="button"
          className="border-rule mt-4 rounded-lg border px-4 text-sm font-bold"
          onClick={onRemove}
        >
          {es.setup.removeChild}
        </button>
      ) : null}
    </fieldset>
  )
}
